import React, { useState } from 'react';
import { DollarSign, MapPin, Calendar, User, Mail, Phone, Building2, AlertCircle, Info } from 'lucide-react';
import { callWFAPI } from '../utils/helper';
export function RentalSummary({
  selections,
  subtotal,
  tax,
  total,
  onCaliforniaAddressChange,
  prices,
  onRentalSubmitted,
  discountSelections
}) {
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    shippingLocation: '',
    eventName: '',
    proposedEventDate: '',
    alternateDate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const hasSelections = selections.fullGen3Ring > 0 || selections.gen3Body > 0 || selections.gen3Head > 0 || selections.gen3JudgesOnly > 0;
  const isFormValid = hasSelections && customerInfo.name && customerInfo.email && customerInfo.phone && customerInfo.shippingLocation && customerInfo.eventName && customerInfo.proposedEventDate;
  const handleInputChange = (field, value) => {
    setCustomerInfo(prev => ({
      ...prev,
      [field]: value
    }));
    if (field === 'shippingLocation') {
      const isCA = value.toLowerCase().includes('california') || value.toLowerCase().includes(', ca');
      onCaliforniaAddressChange(isCA);
    }
  };
  const handleSubmit = async () => {
    if (!isFormValid) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const data = {
        appname: 'tkd-score',
        formname: 'Gen_3_Scoring_Rental',
        Name: customerInfo.name,
        Email: customerInfo.email,
        Phone_Number: customerInfo.phone,
        Shipping_Location: customerInfo.shippingLocation,
        Event_Name: customerInfo.eventName,
        Proposed_Event_Date: customerInfo.proposedEventDate,
        Alternate_Date: customerInfo.alternateDate || '',
        Gen3_Body_Quantity: selections.gen3Body,
        Gen3_Head_Quantity: selections.gen3Head,
        Gen3_Judges_Only_Quantity: selections.gen3JudgesOnly,
        Full_Gen3_Ring_Quantity: selections.fullGen3Ring,
        Requires_Technician: selections.requireTech ? 'true' : 'false',
        Subtotal_Amount: subtotal.toFixed(2),
        Tax_Amount: tax.toFixed(2),
        Total_Amount: total.toFixed(2),
        Event_Shirts_Discount: discountSelections.eventShirts ? 'true' : 'false',
        Collegiate_Discount: discountSelections.collegiate ? 'true' : 'false'
      };
      await callWFAPI(0, 'zohoCreator', data);
      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
        setCustomerInfo({
          name: '',
          email: '',
          phone: '',
          shippingLocation: '',
          eventName: '',
          proposedEventDate: '',
          alternateDate: ''
        });
        if (onRentalSubmitted) {
          onRentalSubmitted();
        }
      }, 3000);
    } catch (error) {
      console.error('Error submitting rental:', error);
      setSubmitError('Failed to submit rental request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="bg-white p-6 rounded-lg shadow-md sticky top-6" data-id="element-525">
      <h2 className="text-xl font-semibold mb-6 text-blue-800 flex items-center" data-id="element-526">
        <DollarSign className="mr-2" size={24} data-id="element-527" />
        Rental Summary
      </h2>

      {/* Price Breakdown */}
      <div className="space-y-3 mb-6 pb-6 border-b border-slate-200" data-id="element-528">
        {selections.fullGen3Ring > 0 && <div className="flex justify-between text-sm" data-id="element-529">
            <span className="text-slate-600" data-id="element-530">
              Full Gen 3 Ring × {selections.fullGen3Ring}
            </span>
            <span className="font-medium text-slate-800" data-id="element-531">
              ${(selections.fullGen3Ring * prices.fullGen3Ring).toFixed(2)}
            </span>
          </div>}
        {selections.gen3Body > 0 && <div className="flex justify-between text-sm" data-id="element-532">
            <span className="text-slate-600" data-id="element-533">
              Gen 3 Body × {selections.gen3Body}
            </span>
            <span className="font-medium text-slate-800" data-id="element-534">
              ${(selections.gen3Body * prices.gen3Body).toFixed(2)}
            </span>
          </div>}
        {selections.gen3Head > 0 && <div className="flex justify-between text-sm" data-id="element-535">
            <span className="text-slate-600" data-id="element-536">
              Gen 3 Head × {selections.gen3Head}
            </span>
            <span className="font-medium text-slate-800" data-id="element-537">
              ${(selections.gen3Head * prices.gen3Head).toFixed(2)}
            </span>
          </div>}
        {selections.gen3JudgesOnly > 0 && <div className="flex justify-between text-sm" data-id="element-538">
            <span className="text-slate-600" data-id="element-539">
              Judges Only × {selections.gen3JudgesOnly}
            </span>
            <span className="font-medium text-slate-800" data-id="element-540">
              ${(selections.gen3JudgesOnly * prices.gen3JudgesOnly).toFixed(2)}
            </span>
          </div>}
        {selections.requireTech && <div className="flex justify-between text-sm" data-id="element-541">
            <span className="text-slate-600" data-id="element-542">On-site Technician</span>
            <span className="font-medium text-slate-800" data-id="element-543">
              ${prices.technician.toFixed(2)}
            </span>
          </div>}

        <div className="flex justify-between text-sm pt-3 border-t border-slate-100" data-id="element-544">
          <span className="text-slate-600" data-id="element-545">Subtotal</span>
          <span className="font-medium text-slate-800" data-id="element-546">
            ${subtotal.toFixed(2)}
          </span>
        </div>

        {tax > 0 && <div className="flex justify-between text-sm" data-id="element-547">
            <span className="text-slate-600" data-id="element-548">Tax</span>
            <span className="font-medium text-slate-800" data-id="element-549">
              ${tax.toFixed(2)}
            </span>
          </div>}

        <div className="flex justify-between text-lg font-bold pt-3 border-t border-slate-200" data-id="element-550">
          <span className="text-slate-800" data-id="element-551">Estimated Total</span>
          <span className="text-blue-800" data-id="element-552">${total.toFixed(2)}</span>
        </div>
      </div>

      {/* Important Notice */}
      <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg" data-id="element-553">
        <div className="flex items-start" data-id="element-554">
          <Info className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-555" />
          <div data-id="element-556">
            <h4 className="font-semibold text-blue-900 mb-1" data-id="element-557">
              Final Quote Notice
            </h4>
            <p className="text-sm text-blue-800" data-id="element-558">
              A final quote will be created to include any additional travel
              expenses and taxes.
            </p>
          </div>
        </div>
      </div>

      {/* Customer Information Form */}
      <div className="space-y-4 mb-6" data-id="element-559">
        <h3 className="font-semibold text-slate-800 mb-3" data-id="element-560">Your Information</h3>

        <div data-id="element-561">
          <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center" data-id="element-562">
            <User size={16} className="mr-1" data-id="element-563" />
            Name *
          </label>
          <input type="text" value={customerInfo.name} onChange={e => handleInputChange('name', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Your full name" data-id="element-564" />
        </div>

        <div data-id="element-565">
          <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center" data-id="element-566">
            <Mail size={16} className="mr-1" data-id="element-567" />
            Email *
          </label>
          <input type="email" value={customerInfo.email} onChange={e => handleInputChange('email', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="your.email@example.com" data-id="element-568" />
        </div>

        <div data-id="element-569">
          <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center" data-id="element-570">
            <Phone size={16} className="mr-1" data-id="element-571" />
            Phone *
          </label>
          <input type="tel" value={customerInfo.phone} onChange={e => handleInputChange('phone', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="(555) 123-4567" data-id="element-572" />
        </div>

        <div data-id="element-573">
          <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center" data-id="element-574">
            <Building2 size={16} className="mr-1" data-id="element-575" />
            Event Name *
          </label>
          <input type="text" value={customerInfo.eventName} onChange={e => handleInputChange('eventName', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Tournament or event name" data-id="element-576" />
        </div>

        <div data-id="element-577">
          <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center" data-id="element-578">
            <MapPin size={16} className="mr-1" data-id="element-579" />
            Shipping Location *
          </label>
          <input type="text" value={customerInfo.shippingLocation} onChange={e => handleInputChange('shippingLocation', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="City, State" data-id="element-580" />
        </div>

        <div data-id="element-581">
          <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center" data-id="element-582">
            <Calendar size={16} className="mr-1" data-id="element-583" />
            Proposed Event Date *
          </label>
          <input type="date" value={customerInfo.proposedEventDate} onChange={e => handleInputChange('proposedEventDate', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-584" />
        </div>

        <div data-id="element-585">
          <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center" data-id="element-586">
            <Calendar size={16} className="mr-1" data-id="element-587" />
            Alternate Date (Optional)
          </label>
          <input type="date" value={customerInfo.alternateDate} onChange={e => handleInputChange('alternateDate', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-588" />
        </div>
      </div>

      {/* Submit Button */}
      <button onClick={handleSubmit} disabled={!isFormValid || isSubmitting} className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${isFormValid && !isSubmitting ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`} data-id="element-589">
        {isSubmitting ? 'Submitting...' : 'Submit Rental Request'}
      </button>

      {submitSuccess && <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm" data-id="element-590">
          ✓ Rental request submitted successfully! We'll contact you soon.
        </div>}

      {submitError && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm" data-id="element-591">
          {submitError}
        </div>}

      {!hasSelections && <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start" data-id="element-592">
          <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-593" />
          <p className="text-yellow-800 text-sm" data-id="element-594">
            Please select at least one equipment item to continue.
          </p>
        </div>}
    </div>;
}